/*
  c-catalog-form: カタログ請求フォーム

  移植元: Store_Consulting-team-dev（Horizon v4.1.1）の assets/c-catalog-form.js。
  本テーマ（Dawn 2.3.0）には Horizon の importmap（@theme/component）が無いため、
  必要な機能だけを持つ最小シム Component をこのファイル内で定義している。
  バリデーション・郵便番号補完・確認パネルなどのロジック本体は移植元から無改変。

  ファイル全体を IIFE で包み、同一ページに複数回読み込まれても
  クラスの二重宣言・customElements の二重登録が起きないようにしている。
*/
(() => {
if (customElements.get('catalog-form-component')) return;

/**
 * Horizon の @theme/component の最小互換シム。
 *
 * - this.refs.<name> : 子孫の ref="<name>" 要素への参照
 *   （このフォームの DOM はパネルの hidden 切替以外に増減しないため、connectedCallback 時に
 *     一度だけ収集してキャッシュする。入力のたびに全走査するコストを避けるため）
 * - data-on-click / data-on-change : 属性値と同名のメソッドを呼ぶ宣言的イベントバインド
 *   （Horizon の on:click="/method" 記法の置き換え）
 */
class Component extends HTMLElement {
  /** @type {Record<string, HTMLElement>} */
  refs = {};

  /** @type {Array<[string, EventListener]>} */
  #delegatedListeners = [];

  connectedCallback() {
    this.refs = this.#collectRefs();
    this.#bindDeclarativeEvents();
  }

  disconnectedCallback() {
    for (const [type, listener] of this.#delegatedListeners) {
      this.removeEventListener(type, listener);
    }
    this.#delegatedListeners = [];
  }

  #collectRefs() {
    /** @type {Record<string, HTMLElement>} */
    const refs = {};
    for (const element of this.querySelectorAll('[ref]')) {
      const name = element.getAttribute('ref');
      if (name && !(name in refs) && element instanceof HTMLElement) refs[name] = element;
    }
    return refs;
  }

  #bindDeclarativeEvents() {
    for (const type of ['click', 'change']) {
      const attribute = `data-on-${type}`;
      /** @type {EventListener} */
      const listener = (event) => {
        const target = event.target instanceof Element ? event.target.closest(`[${attribute}]`) : null;
        if (!target || !this.contains(target)) return;

        const methodName = target.getAttribute(attribute);
        if (!methodName) return;

        const method = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (this))[methodName];
        if (typeof method === 'function') method.call(this, event);
      };
      this.addEventListener(type, listener);
      this.#delegatedListeners.push([type, listener]);
    }
  }
}

/**
 * 資料送付先の一時タグの接頭辞。Flow が住所を反映したあとに削除するため、
 * 顧客に残り続けることはない（年度タグ catalog-request-YYYY とは別系統）。
 */
const SHIP_TAG_PREFIX = 'cs-';

/**
 * 一時タグに載せる住所項目。[タグ接頭辞, note のキー] の順で、Flow 側の復元と 1:1 で対応する。
 */
const SHIP_TAG_FIELDS = [
  ['cs-zip', '郵便番号'],
  ['cs-pref', '都道府県'],
  ['cs-city', '市区町村'],
  ['cs-addr1', '地名番地'],
  ['cs-addr2', '建物名'],
  ['cs-tel', '電話番号'],
];

/**
 * 一時タグに載せる請求者の姓・名。[タグ接頭辞, input の name] の順で、Flow の Run code（cs-last / cs-first）と対応する。
 *
 * form 'customer' は既存顧客の姓名を contact[last_name] / contact[first_name] で上書きしない
 * （2026-09-25 実測: メールアドレスだけで登録済みだった顧客の名前が空のまま残った）。
 * 住所と同じくタグにも載せ、Flow が顧客の姓名と宛名メタフィールドを毎回上書きする。
 * contact[last_name] / contact[first_name] の送信はそのまま残す（新規顧客にはそこから入る）。
 */
const NAME_TAG_FIELDS = [
  ['cs-last', 'contact[last_name]'],
  ['cs-first', 'contact[first_name]'],
];

/**
 * 「お知らせを受け取らない」を選んだときだけ付ける一時タグ。
 *
 * Shopify の form 'customer' は送信内容に関係なく顧客を必ずメール購読状態にする
 * （公式ドキュメント: "the accepts_marketing attribute of the associated customer object
 * will be set to true"）。contact[accepts_marketing] を false で送っても、送らなくても変わらない。
 * そのため購読解除は送信後に Flow 側で行うしかなく、その判定材料としてこのタグを渡す。
 * 住所の cs-* タグと同じく、Flow が処理後に削除する。
 */
const NEWSLETTER_OPT_OUT_TAG = 'cs-news:no';

/**
 * 年度タグ（catalog-request-YYYY）の接頭辞。
 */
const YEAR_TAG_PREFIX = 'catalog-request-';

/**
 * カタログ請求フォーム（c-catalog-form セクション）。
 *
 * - クライアントバリデーション（必須 / 形式 / メール一致）
 *   - blur 時インライン検証（空欄は責めない = punish late）、修正時は即時クリア（reward early）
 *   - 送信時は GOV.UK パターンのエラーサマリー（件数 + 各欄へのアンカー）
 * - 郵便番号 → 住所自動入力（zipcloud、失敗しても送信は阻害しない）
 *   - 仕様（カタログ請求 - 仕様シート）: ハイフン・「〒」・桁数違いは自動除去せずエラー表示。
 *     全角数字 → 半角の変換のみ入力補助として blur 時に行う
 * - 送信前の入力内容確認ステップ（同一ページ内パネル切替 + history 連動 + ステップ表示同期）
 * - 「お知らせ」ラジオと購読状態の同期
 *   （「受け取らない」時は警告表示 + cs-news:no タグを付与。実際の購読解除は Flow が行う。
 *     contact[accepts_marketing] は Shopify 側で無視されるため送信値に意味はない）
 * - プライバシー同意 checkbox（初期ON）: チェックを外すと警告表示（仕様確定事項）
 *
 * @typedef {Object} CatalogFormRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLDivElement} [editPanel]
 * @property {HTMLDivElement} [confirmPanel]
 * @property {HTMLHeadingElement} [confirmHeading]
 * @property {HTMLDListElement} [confirmList]
 * @property {HTMLButtonElement} [confirmButton]
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLButtonElement} [backButton]
 * @property {HTMLSpanElement} [submitLabel]
 * @property {HTMLSpanElement} [submitSpinner]
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [emailConfirm]
 * @property {HTMLInputElement} [phone]
 * @property {HTMLInputElement} [zip]
 * @property {HTMLInputElement} [prefecture]
 * @property {HTMLInputElement} [city]
 * @property {HTMLInputElement} [address1]
 * @property {HTMLInputElement} [acceptsMarketing]
 * @property {HTMLInputElement} [customerTag]
 * @property {HTMLDivElement} [declineWarning]
 * @property {HTMLDivElement} [privacyWarning]
 * @property {HTMLParagraphElement} [zipStatus]
 * @property {HTMLDivElement} [errorSummary]
 * @property {HTMLSpanElement} [errorSummaryTitle]
 * @property {HTMLUListElement} [errorSummaryList]
 * @property {HTMLOListElement} [stepIndicator]
 * @property {HTMLDivElement} [liveRegion]
 */

/**
 * @extends {Component<CatalogFormRefs>}
 */
class CatalogFormComponent extends Component {
  #confirmShown = false;
  #pushedState = false;
  #lastZipLookup = '';

  /** @type {AbortController | null} */
  #zipAbort = null;

  /** @type {((event: SubmitEvent) => void) | null} */
  #onSubmitBound = null;
  /** @type {((event: PopStateEvent) => void) | null} */
  #onPopStateBound = null;
  /** @type {((event: FocusEvent) => void) | null} */
  #onFocusOutBound = null;
  /** @type {((event: Event) => void) | null} */
  #onInputBound = null;
  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeyDownBound = null;
  /** @type {((event: MouseEvent) => void) | null} */
  #onSummaryClickBound = null;

  connectedCallback() {
    super.connectedCallback();

    // 送信完了表示のみのとき（form.posted_successfully?）はフォームが無い
    if (!this.refs.form || !this.refs.editPanel) return;

    this.#onSubmitBound = (event) => this.#onSubmit(event);
    this.refs.form.addEventListener('submit', this.#onSubmitBound);

    this.#onPopStateBound = (event) => this.#onPopState(event);
    window.addEventListener('popstate', this.#onPopStateBound);

    this.#onFocusOutBound = (event) => this.#onFocusOut(event);
    this.addEventListener('focusout', this.#onFocusOutBound);

    this.#onInputBound = (event) => this.#onInput(event);
    this.addEventListener('input', this.#onInputBound);

    // Enter による意図しない form submit を完全ブロック（IME 変換確定・hCaptcha 干渉対策）
    this.#onKeyDownBound = (event) => this.#onKeyDown(event);
    this.addEventListener('keydown', this.#onKeyDownBound);

    if (this.refs.errorSummary) {
      this.#onSummaryClickBound = (event) => this.#onSummaryClick(event);
      this.refs.errorSummary.addEventListener('click', this.#onSummaryClickBound);
    }

    this.syncMarketingConsent();
    this.syncPrivacyWarning();
    this.syncCustomerTag();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#onSubmitBound && this.refs.form) {
      this.refs.form.removeEventListener('submit', this.#onSubmitBound);
      this.#onSubmitBound = null;
    }

    if (this.#onPopStateBound) {
      window.removeEventListener('popstate', this.#onPopStateBound);
      this.#onPopStateBound = null;
    }

    if (this.#onFocusOutBound) {
      this.removeEventListener('focusout', this.#onFocusOutBound);
      this.#onFocusOutBound = null;
    }

    if (this.#onInputBound) {
      this.removeEventListener('input', this.#onInputBound);
      this.#onInputBound = null;
    }

    if (this.#onKeyDownBound) {
      this.removeEventListener('keydown', this.#onKeyDownBound);
      this.#onKeyDownBound = null;
    }

    if (this.#onSummaryClickBound && this.refs.errorSummary) {
      this.refs.errorSummary.removeEventListener('click', this.#onSummaryClickBound);
      this.#onSummaryClickBound = null;
    }

    this.#zipAbort?.abort();
  }

  /**
   * 入力内容を検証し、確認パネルへ切り替える。
   * 確認ボタンの on:click と、入力パネル表示中の Enter 送信から呼ばれる。
   */
  showConfirmation() {
    if (!this.#validateAll()) return;

    this.#buildConfirmList();
    this.#togglePanels(true);

    if (!window.Shopify?.designMode) {
      history.pushState({ ccfConfirm: true }, '');
      this.#pushedState = true;
    }

    this.refs.confirmHeading?.focus();
    this.#announce(this.dataset.msgConfirmShown);
  }

  /**
   * 確認パネルから入力パネルへ戻る（修正ボタンの on:click）。
   */
  backToEdit() {
    if (this.#pushedState) {
      // popstate ハンドラ経由で入力パネルへ戻し、履歴を汚さない
      history.back();
    } else {
      this.#togglePanels(false);
      this.#announce(this.dataset.msgEditShown);
    }
  }

  /**
   * 「お知らせ」ラジオの選択と contact[accepts_marketing] hidden を同期し、
   * 「受け取らない」選択時のみ警告ボックスを表示する。
   *
   * hidden の送信有無は購読状態を左右しない。form 'customer' は送信内容に関係なく
   * 顧客を購読状態にするため（2026-07-28 クライアント報告 + 公式ドキュメントで確認）、
   * 「受け取らない」の反映は syncShippingTags が付ける cs-news:no タグ経由で Flow が行う。
   * hidden 自体は Shopify が将来 accepts_marketing を尊重した場合に正しい値が渡るよう残している。
   */
  syncMarketingConsent() {
    const { acceptsMarketing, declineWarning } = this.refs;

    const checked = this.querySelector('input[name="contact[note][お知らせ]"]:checked');
    const optIn = checked ? checked.value === this.dataset.optInValue : true;

    if (acceptsMarketing) acceptsMarketing.disabled = !optIn;
    if (declineWarning) declineWarning.hidden = optIn;
  }

  /**
   * 「カタログの種類」ラジオの選択と contact[tags] hidden を同期する。
   * 年度ごとに別タグを付けるため、選択肢（catalog_type_option ブロック）側に
   * data-customer-tag を持たせ、選ばれたものを hidden へ流し込む。
   * data-customer-tag が無い選択肢では hidden を触らない（Liquid 側の既定値が残る）。
   */
  syncCustomerTag() {
    const { customerTag } = this.refs;
    if (!customerTag) return;

    const checked = this.querySelector('input[name="contact[note][カタログの種類]"]:checked');
    const tag = checked && checked.dataset.customerTag;
    if (tag) customerTag.value = tag;
  }

  /**
   * 住所 6 項目・姓名・「お知らせ」の選択を一時タグとして contact[tags] へ載せる
   * （送信直前に #onSubmit から呼ぶ）。
   *
   * Shopify の form 'customer' は、送信されたメールアドレスの顧客が既に存在する場合
   * contact[note] を書き込まない（2026-07-27 実測。既存顧客 2 件とも note が空のままだった）。
   * note だけを頼りにすると既存顧客の住所がどこにも残らないため、タグ経由でも住所を渡す。
   * タグは既存顧客でも確実に更新される。反映後は Flow が cs-* タグを削除する。
   *
   * カンマは contact[tags] のセパレータなので値から除去する（日本の住所ではまず現れないが、
   * 建物名に紛れ込むとタグが分裂して住所が壊れるため）。
   */
  syncShippingTags() {
    const { customerTag } = this.refs;
    if (!customerTag) return;

    // 年度タグ（catalog-request-YYYY）は残し、前回組み立てた cs-* だけ捨てて作り直す
    const baseTags = customerTag.value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag && !tag.startsWith(SHIP_TAG_PREFIX));

    const shippingTags = [];
    for (const [prefix, noteKey] of SHIP_TAG_FIELDS) {
      const field = this.querySelector(`[name="contact[note][${noteKey}]"]`);
      if (!(field instanceof HTMLInputElement)) continue;

      const value = field.value.trim().replace(/,/g, '');
      if (!value) continue;

      // タグは 1 本 255 文字まで。日本の住所では起こらないが、超過分で全体が落ちないよう切り詰める
      shippingTags.push(`${prefix}:${value}`.slice(0, 255));
    }

    // 姓・名。カンマ（タグの区切り）に加え、" と \ も除く。Flow はこの値を Liquid で
    // customerUpdate の JSON に埋め込むため、残っていると JSON が壊れて更新が失敗する
    for (const [prefix, name] of NAME_TAG_FIELDS) {
      const field = this.querySelector(`[name="${name}"]`);
      if (!(field instanceof HTMLInputElement)) continue;

      const value = field.value.replace(/[,"\\]/g, '').trim();
      if (!value) continue;

      shippingTags.push(`${prefix}:${value}`.slice(0, 255));
    }

    // 「受け取らない」時だけ購読解除用のタグを足す。「受け取る」は Shopify の既定動作
    // （必ず購読状態になる）と一致するため、タグを付けず Flow も動かさない
    const newsletter = this.querySelector('input[name="contact[note][お知らせ]"]:checked');
    if (newsletter instanceof HTMLInputElement && newsletter.value !== this.dataset.optInValue) {
      shippingTags.push(NEWSLETTER_OPT_OUT_TAG);
    }

    // 今回選ばれた年度も一時タグに載せる（Flow の受付番号・カタログ送信日の判定に使う）。
    // contact[tags] は既存顧客に対して「追加」であって置き換えではないため、年度をまたいで
    // 請求した顧客には catalog-request-2027 と -2028 が両方残る。Flow が見る customer.tags からは
    // 「今回どちらを請求したか」を区別できない（タグの追加順も取れない）。
    // cs-* は送信のたびに作り直されて Flow が消すため、これが今回の年度を示す唯一の手がかりになる。
    const yearTag = baseTags.find((tag) => tag.startsWith(YEAR_TAG_PREFIX));
    if (yearTag) shippingTags.push(`cs-year:${yearTag.slice(YEAR_TAG_PREFIX.length)}`);

    customerTag.value = baseTags.concat(shippingTags).join(',');
  }

  /**
   * プライバシー同意 checkbox と警告ボックスを同期する（on:change + 初期化時）。
   * チェックを外すと警告表示、チェックすると警告とエラーをクリア（仕様: デフォルトON + 解除時アラート）。
   */
  syncPrivacyWarning() {
    const { privacyWarning } = this.refs;
    const checkbox = this.querySelector('input[type="checkbox"][name="contact[note][プライバシーポリシー同意]"]');
    if (!(checkbox instanceof HTMLInputElement)) return;

    if (privacyWarning) privacyWarning.hidden = checkbox.checked;
    if (checkbox.checked) this.#clearFieldError(checkbox);
  }

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit(event) {
    // event.submitter が null = Enter キーによる暗黙的 submit or form.submit()。
    // 意図しない送信を完全に防ぐため、常にブロック（送信はボタンクリック経由のみ許可）。
    if (!event.submitter) {
      event.preventDefault();
      return;
    }

    if (!this.#confirmShown) {
      // 入力パネル表示中の submit は無視（次画面遷移は「確認画面へ」ボタン経由のみ）
      event.preventDefault();
      return;
    }

    this.syncMarketingConsent();
    this.syncCustomerTag();
    this.syncShippingTags();

    // 二重送信防止（連打対策）+ 送信中の可視化。hCaptcha チャレンジをキャンセルした場合に
    // フォームが詰まないよう、数秒後に自動でロックを解除する
    const { submitButton, submitLabel, submitSpinner, backButton } = this.refs;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      if (backButton) backButton.disabled = true;
      if (submitLabel) {
        if (!submitLabel.dataset.originalText) submitLabel.dataset.originalText = submitLabel.textContent || '';
        submitLabel.textContent = this.dataset.msgSending || submitLabel.dataset.originalText;
      }
      if (submitSpinner) submitSpinner.hidden = false;

      setTimeout(() => {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        if (backButton) backButton.disabled = false;
        if (submitLabel && submitLabel.dataset.originalText) {
          submitLabel.textContent = submitLabel.dataset.originalText;
        }
        if (submitSpinner) submitSpinner.hidden = true;
      }, 5000);
    }
  }

  /**
   * @param {PopStateEvent} event
   */
  #onPopState(event) {
    if (!this.#confirmShown) return;
    if (event.state?.ccfConfirm) return;

    this.#pushedState = false;
    this.#togglePanels(false);
    this.#announce(this.dataset.msgEditShown);
    this.refs.confirmButton?.focus();
  }

  /**
   * blur（focusout）でのインライン検証。
   * - zip / phone: 全角数字 → 半角の変換のみ実施（数字以外の自動除去はしない = 仕様）
   * - 空欄の必須エラーは出さない（punish late）。形式エラーとメール一致のみ即時表示
   * @param {FocusEvent} event
   */
  #onFocusOut(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type === 'radio' || target.type === 'checkbox' || target.type === 'hidden') return;

    const { zip, phone, email, emailConfirm } = this.refs;

    if (target === zip || target === phone) {
      const converted = this.#toHalfWidthDigits(target.value);
      if (converted !== target.value) target.value = converted;
      if (target === zip) this.#onZipInput();
    }

    const message = this.#validateField(target, { showRequired: false });
    if (message) {
      this.#setFieldError(target, message);
    } else {
      this.#clearFieldError(target);
    }

    // メール欄の blur では確認欄側の一致状態も更新する
    if (target === email && emailConfirm?.value) {
      const confirmMessage = this.#validateField(emailConfirm, { showRequired: false });
      if (confirmMessage) this.#setFieldError(emailConfirm, confirmMessage);
      else this.#clearFieldError(emailConfirm);
    }
  }

  /**
   * text 系 input 内での Enter キー押下を無効化する（IME 変換確定・hCaptcha 干渉による
   * 意図しない form submit を防ぐ）。ボタン・radio・checkbox・textarea は対象外。
   * @param {KeyboardEvent} event
   */
  #onKeyDown(event) {
    if (event.key !== 'Enter' && event.keyCode !== 13) return;
    // IME 変換中の Enter（変換確定）は元々 form submit を発火しないが念のためスキップ
    if (event.isComposing || event.keyCode === 229) return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const type = target.type;
    // ボタン・チェックボックス・ラジオでの Enter は本来の挙動を維持
    if (type === 'button' || type === 'submit' || type === 'reset') return;
    if (type === 'checkbox' || type === 'radio') return;

    // text/email/tel/number/search/url/password 等での Enter による implicit submit を無効化
    event.preventDefault();
  }

  /**
   * 入力のたびの後追い処理（reward early）。
   * - エラー中の欄が修正されたら即座にエラーを消す
   * - 自動入力ハイライトはユーザーが触ったら解除
   * @param {Event} event
   */
  #onInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.classList.contains('is-autofilled')) target.classList.remove('is-autofilled');

    // 郵便番号は 7 桁揃った時点で即座に住所検索する（blur を待たない）
    if (target === this.refs.zip) this.#onZipInput();

    if (target.type === 'radio' || target.type === 'checkbox') {
      if (target.checked) this.#clearFieldError(target);
      return;
    }

    if (target.getAttribute('aria-invalid') === 'true') {
      const message = this.#validateField(target, { showRequired: true });
      if (!message) this.#clearFieldError(target);
    }
  }

  /**
   * 全角数字を半角に変換する（それ以外の文字は変更しない = 仕様「自動除去しない」）。
   * @param {string} value
   * @returns {string}
   */
  #toHalfWidthDigits(value) {
    return value.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
  }

  /**
   * zipcloud 検索トリガー判定用の内部正規化（表示値には使わない）。
   * @param {string} value
   * @returns {string}
   */
  #digitsOnly(value) {
    return this.#toHalfWidthDigits(value).replace(/[^0-9]/g, '');
  }

  #emailsMatch() {
    const { email, emailConfirm } = this.refs;
    if (!email || !emailConfirm) return true;
    return email.value.trim().toLowerCase() === emailConfirm.value.trim().toLowerCase();
  }

  /**
   * 単一フィールドの検証。エラーメッセージ（無ければ空文字）を返す。
   * 郵便番号は仕様どおり原因別の具体的な文言を出し分ける。
   * @param {HTMLInputElement} input
   * @param {{ showRequired: boolean }} options
   * @returns {string}
   */
  #validateField(input, { showRequired }) {
    const requiredMsg = input.dataset.msgRequired || this.dataset.msgRequired || '';

    if (input.type === 'checkbox') {
      if (input.required && !input.checked) return showRequired ? requiredMsg : '';
      return '';
    }

    const value = input.value.trim();

    if (value === '') {
      return input.required && showRequired ? requiredMsg : '';
    }

    if (input === this.refs.zip) {
      if (value.includes('〒')) return this.dataset.msgZipMark || '';
      if (/[-−ー－‐]/.test(value)) return this.dataset.msgZipHyphen || '';
      if (/[^0-9]/.test(value) || value.length !== 7) return this.dataset.msgZipFormat || '';
      return '';
    }

    if (input.validity.typeMismatch) {
      return input.dataset.msgPattern || this.dataset.msgEmail || '';
    }
    if (input.validity.patternMismatch || input.validity.tooShort || input.validity.tooLong) {
      return input.dataset.msgPattern || this.dataset.msgPattern || '';
    }

    if (input === this.refs.emailConfirm && this.refs.email?.value && !this.#emailsMatch()) {
      return this.dataset.msgEmailMismatch || '';
    }

    return '';
  }

  #onZipInput() {
    const { zip } = this.refs;
    if (!zip) return;

    const normalized = this.#digitsOnly(zip.value);
    if (normalized.length === 7 && normalized !== this.#lastZipLookup) {
      this.#lookupAddress(normalized);
    }
  }

  /**
   * zipcloud API で郵便番号から住所を自動入力する。
   * 既に入力済みの欄は上書きしない。失敗しても送信は阻害しない。
   * 成功時は自動入力した欄をハイライトし、ステータスで明示する（成功フィードバック）。
   * @param {string} zipCode
   */
  async #lookupAddress(zipCode) {
    this.#lastZipLookup = zipCode;

    const { zipStatus, prefecture, city, address1 } = this.refs;
    if (zipStatus) {
      zipStatus.hidden = false;
      zipStatus.classList.remove('is-success');
      zipStatus.textContent = this.dataset.msgZipSearching || '';
    }

    this.#zipAbort?.abort();
    this.#zipAbort = new AbortController();
    const timeoutId = setTimeout(() => this.#zipAbort?.abort(new DOMException('timeout', 'TimeoutError')), 5000);

    try {
      const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipCode}`, {
        signal: this.#zipAbort.signal,
      });
      const data = await response.json();
      const result = data?.results?.[0];

      if (!result) {
        if (zipStatus) zipStatus.textContent = this.dataset.msgZipNotFound || '';
        return;
      }

      const filled = [];
      if (prefecture && !prefecture.value) {
        prefecture.value = result.address1 || '';
        filled.push(prefecture);
      }
      if (city && !city.value) {
        city.value = result.address2 || '';
        filled.push(city);
      }
      if (address1 && !address1.value) {
        address1.value = result.address3 || '';
        filled.push(address1);
      }

      for (const input of filled) {
        input.classList.add('is-autofilled');
        this.#clearFieldError(input);
      }

      if (zipStatus) {
        zipStatus.hidden = false;
        zipStatus.classList.add('is-success');
        zipStatus.textContent = this.dataset.msgZipFilled || '';
      }
      this.#announce(this.dataset.msgZipFilled);
    } catch (error) {
      // 新しい入力による中断は無視。タイムアウト・通信失敗は手入力を案内
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (zipStatus) {
        zipStatus.classList.remove('is-success');
        zipStatus.textContent = this.dataset.msgZipNotFound || '';
      }
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * 入力パネル全体を検証する。
   * 失敗時は GOV.UK パターン: エラーサマリーに件数とアンカーリンクを表示してフォーカスを移す。
   * @returns {boolean} すべて有効なら true
   */
  #validateAll() {
    this.#clearAllErrors();

    /** @type {HTMLInputElement[]} */
    const invalid = [];
    const seenRadioGroups = new Set();
    const inputs = this.refs.editPanel?.querySelectorAll('input:not([type="hidden"])') ?? [];

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement) || input.disabled) continue;

      if (input.type === 'radio') {
        if (seenRadioGroups.has(input.name)) continue;
        seenRadioGroups.add(input.name);

        const group = Array.from(
          this.refs.editPanel?.querySelectorAll(`input[type="radio"][name="${CSS.escape(input.name)}"]`) ?? []
        );
        const required = group.some((radio) => radio instanceof HTMLInputElement && radio.required);
        const checked = group.some((radio) => radio instanceof HTMLInputElement && radio.checked);

        if (required && !checked) {
          this.#setFieldError(input, input.dataset.msgRequired || this.dataset.msgRequired);
          invalid.push(input);
        }
        continue;
      }

      const message = this.#validateField(input, { showRequired: true });
      if (message) {
        this.#setFieldError(input, message);
        invalid.push(input);
      }
    }

    const { errorSummary } = this.refs;

    if (invalid.length > 0) {
      const summaryText = this.#renderErrorSummary(invalid);
      if (errorSummary) {
        errorSummary.hidden = false;
        errorSummary.focus();
        errorSummary.scrollIntoView({ block: 'start', behavior: this.#motionSafe() ? 'smooth' : 'auto' });
      }
      this.#announce(summaryText);
      return false;
    }

    if (errorSummary) errorSummary.hidden = true;
    return true;
  }

  /**
   * エラーサマリーを組み立てる。タイトルの {count} は件数に置換。
   * @param {HTMLInputElement[]} invalidFields
   * @returns {string} サマリーのタイトル文言
   */
  #renderErrorSummary(invalidFields) {
    const { errorSummaryTitle, errorSummaryList } = this.refs;
    const template = this.dataset.msgSummary || '';
    const text = template.replace('{count}', String(invalidFields.length));

    if (errorSummaryTitle) errorSummaryTitle.textContent = text;
    if (!errorSummaryList) return text;

    errorSummaryList.textContent = '';
    for (const input of invalidFields) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${input.id}`;
      const label = this.#fieldLabelText(input);
      const messageText = this.#fieldErrorElement(input)?.querySelector('span')?.textContent || '';
      link.textContent = label ? `${label}: ${messageText}` : messageText;
      li.append(link);
      errorSummaryList.append(li);
    }
    return text;
  }

  /**
   * エラーサマリーのアンカークリックで該当フィールドへフォーカス移動する。
   * @param {MouseEvent} event
   */
  #onSummaryClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!link) return;

    event.preventDefault();
    const id = decodeURIComponent((link.getAttribute('href') || '').slice(1));
    const target = document.getElementById(id);
    if (!(target instanceof HTMLElement)) return;

    target.focus({ preventScroll: true });
    const scrollTarget =
      target.closest('.c-catalog-form__radio-option, .c-catalog-form__checkbox-option') || target;
    scrollTarget.scrollIntoView({ block: 'center', behavior: this.#motionSafe() ? 'smooth' : 'auto' });
  }

  /**
   * サマリーリンク表示用のラベル文字列（バッジを除いた見出し）を返す。
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  #fieldLabelText(input) {
    const container = input.closest('.c-catalog-form__field, .c-catalog-form__fieldset');
    const labelText = container?.querySelector('.c-catalog-form__label-text');
    return labelText?.textContent?.trim() || '';
  }

  /**
   * @returns {boolean} アニメーションしてよければ true
   */
  #motionSafe() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * input（または radio/checkbox グループの先頭 input）に対応するエラー領域を返す。
   * @param {HTMLInputElement} input
   * @returns {HTMLElement | null}
   */
  #fieldErrorElement(input) {
    return input.closest('.c-catalog-form__field, .c-catalog-form__fieldset')?.querySelector('.c-catalog-form__field-error') ?? null;
  }

  /**
   * aria-describedby はヒント・注記の既存値を保持したままエラー ID を追記する
   * （上書きするとヒントとの関連付けが失われるため）。
   * @param {HTMLInputElement} input
   * @param {string | undefined} message
   */
  #setFieldError(input, message) {
    const errorElement = this.#fieldErrorElement(input);
    if (!errorElement) return;

    const text = errorElement.querySelector('span');
    if (text) text.textContent = message || '';
    errorElement.hidden = false;

    input.setAttribute('aria-invalid', 'true');
    if (errorElement.id) {
      if (input.dataset.describedbyBase === undefined) {
        input.dataset.describedbyBase = input.getAttribute('aria-describedby') || '';
      }
      const base = input.dataset.describedbyBase;
      input.setAttribute('aria-describedby', base ? `${base} ${errorElement.id}` : errorElement.id);
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  #clearFieldError(input) {
    const errorElement = this.#fieldErrorElement(input);
    if (errorElement) {
      errorElement.hidden = true;
      const text = errorElement.querySelector('span');
      if (text) text.textContent = '';
    }
    input.removeAttribute('aria-invalid');
    this.#restoreDescribedby(input);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #restoreDescribedby(input) {
    const base = input.dataset.describedbyBase;
    if (base === undefined) return;
    if (base) input.setAttribute('aria-describedby', base);
    else input.removeAttribute('aria-describedby');
  }

  #clearAllErrors() {
    const { editPanel, errorSummary } = this.refs;
    if (!editPanel) return;

    for (const errorElement of editPanel.querySelectorAll('.c-catalog-form__field-error')) {
      errorElement.hidden = true;
      const text = errorElement.querySelector('span');
      if (text) text.textContent = '';
    }
    for (const input of editPanel.querySelectorAll('input[aria-invalid]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.removeAttribute('aria-invalid');
      this.#restoreDescribedby(input);
    }
    if (errorSummary) errorSummary.hidden = true;
  }

  /**
   * data-confirm-label 付きの入力を DOM 順に走査し、確認リスト（dl）を組み立てる。
   * 値は必ず textContent で追加する（HTML として解釈させない）。
   */
  #buildConfirmList() {
    const { confirmList, editPanel } = this.refs;
    if (!confirmList || !editPanel) return;

    confirmList.textContent = '';
    const seenRadioGroups = new Set();

    for (const field of editPanel.querySelectorAll('[data-confirm-label]')) {
      if (!(field instanceof HTMLInputElement)) continue;

      const label = field.dataset.confirmLabel || '';
      let value = '';

      if (field.type === 'radio') {
        if (seenRadioGroups.has(field.name)) continue;
        seenRadioGroups.add(field.name);

        const checked = editPanel.querySelector(`input[type="radio"][name="${CSS.escape(field.name)}"]:checked`);
        value = checked instanceof HTMLInputElement ? checked.dataset.confirmText || checked.value : '—';
      } else if (field.type === 'checkbox') {
        value = field.checked ? field.dataset.confirmText || field.value : '—';
      } else {
        value = field.value.trim() || '—';
      }

      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      confirmList.append(dt, dd);
    }
  }

  /**
   * @param {boolean} showConfirm
   */
  #togglePanels(showConfirm) {
    const { editPanel, confirmPanel } = this.refs;
    if (!editPanel || !confirmPanel) return;

    this.#confirmShown = showConfirm;
    editPanel.hidden = showConfirm;
    confirmPanel.hidden = !showConfirm;

    this.#updateSteps(showConfirm ? 2 : 1);

    this.scrollIntoView({ block: 'start' });
  }

  /**
   * ステップインジケーターの現在位置を更新する（1=入力, 2=確認, 3=完了）。
   * @param {number} current
   */
  #updateSteps(current) {
    const { stepIndicator } = this.refs;
    if (!stepIndicator) return;

    const steps = stepIndicator.querySelectorAll('.c-catalog-form__step');
    steps.forEach((step, index) => {
      const number = index + 1;
      step.classList.toggle('is-current', number === current);
      step.classList.toggle('is-done', number < current);
      if (number === current) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
  }

  /**
   * @param {string | undefined} message
   */
  #announce(message) {
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = message || '';
    }
  }
}

customElements.define('catalog-form-component', CatalogFormComponent);
})();
